import React, { useEffect, useState } from 'react';

interface Version {
  id: string;
  content: string;
  timestamp: number;
  preview: string;
}

interface VersionHistoryProps {
  onClose: () => void;
  onRestore: (content: string) => void;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({ onClose, onRestore }) => {
  const [versions, setVersions] = useState<Version[]>([]);

  useEffect(() => {
    const savedVersions = localStorage.getItem('prompt-editor-history');
    if (savedVersions) {
      setVersions(JSON.parse(savedVersions));
    }
  }, []);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `${diffMins}m atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    return date.toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white/95 backdrop-blur-md border border-ghost-200 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-float-up">
          
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-ghost-100">
            <h2 className="text-ghost-800 font-medium">Histórico de Versões</h2>
            <button
              onClick={onClose}
              className="text-ghost-400 hover:text-ghost-600 transition-colors duration-150"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Version list */}
          <div className="max-h-96 overflow-y-auto">
            {versions.length === 0 ? (
              <div className="p-12 text-center text-ghost-400">
                <div className="mb-4">
                  <svg className="w-12 h-12 mx-auto text-ghost-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm">Nenhum histórico disponível</p>
              </div>
            ) : (
              <div className="p-2">
                {versions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => onRestore(version.content)}
                    className="w-full p-4 text-left hover:bg-ghost-50 rounded-lg transition-colors duration-150 group"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-xs text-ghost-400 font-mono">
                        {formatTimestamp(version.timestamp)}
                      </div>
                      <div className="text-xs text-ghost-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        Clique para restaurar
                      </div>
                    </div>
                    <div className="text-sm text-ghost-700 line-clamp-3">
                      {version.preview}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-ghost-100 bg-ghost-50/50">
            <p className="text-xs text-ghost-400 text-center">
              Versões são salvas automaticamente a cada poucos minutos
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};